import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2,
  Briefcase,
  Calendar,
  Users,
  Edit2,
  Trash2,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { API_BASE_URL } from '../lib/api';

interface Activity {
  id: string;
  activityId: string;
  activityTitle: string;
  accountId: string;
  accountName?: string;
  dealId: string | null;
  dealName?: string;
  activityDatetime: string | null;
  activityPurpose: string | null;
  activityBackground: string | null;
  activityBody: string | null;
  activityOutcome: string | null;
  activityNote: string | null;
  tsAttendees: string | null;
  visitTo: string | null;
  meetingMaterials: string | null;
  meetingNextSteps: string | null;
  meetingNextDue: string | null;
  audioFileKey: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Participant {
  id: string;
  personCompany: string | null;
  personDept: string | null;
  personTitle: string | null;
  personName: string | null;
  personRole: string | null;
}

export function ActivityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchActivity();
    } else {
      setError('商談IDが指定されていません');
      setIsLoading(false);
    }
  }, [id]);

  const fetchActivity = async () => {
    if (!id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/activities/${id}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('商談情報の取得に失敗しました');
      }
      
      const data = await response.json();
      setActivity(data.activity);
      setParticipants(data.participants || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('この商談を削除しますか？')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/activities/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('削除に失敗しました');
      }
      
      navigate('/activities');
    } catch (err: any) {
      setError(err.message);
    }
  };


  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-red-500 mb-4">{error || '商談が見つかりません'}</p>
        <Button onClick={() => navigate('/activities')}>商談一覧に戻る</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/activities')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {activity.activityTitle || '(タイトルなし)'}
            </h1>
            <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {formatDateTime(activity.activityDatetime)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            leftIcon={<Edit2 size={16} />}
            onClick={() => navigate(`/activities/${id}/edit`)}
          >
            編集
          </Button>
          <Button 
            variant="danger" 
            leftIcon={<Trash2 size={16} />}
            onClick={handleDelete}
          >
            削除
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* メインコンテンツ */}
        <div className="lg:col-span-2 space-y-6">
          {/* 商談内容 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} />
                商談内容
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {activity.activityBackground && (
                <div>
                  <p className="text-sm mb-2 font-medium" style={{ color: 'var(--text-secondary)' }}>背景</p>
                  <div className="whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
                    {activity.activityBackground}
                  </div>
                </div>
              )}
              {activity.activityBody ? (
                <div>
                  {activity.activityBackground && (
                    <p className="text-sm mb-2 font-medium" style={{ color: 'var(--text-secondary)' }}>商談内容</p>
                  )}
                  <div className="whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
                    {activity.activityBody}
                  </div>
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)' }}>内容が記録されていません</p>
              )}
            </CardContent>
          </Card>

          {/* 結果と次のアクション */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">商談結果</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: activity.activityOutcome ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                  {activity.activityOutcome || '-'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">次のアクション</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: activity.meetingNextSteps ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                  {activity.meetingNextSteps || '-'}
                </p>
                {activity.meetingNextDue && (
                  <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                    期限: {formatDateTime(activity.meetingNextDue)}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* 補足事項 */}
          {activity.activityNote && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">補足事項</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
                  {activity.activityNote}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* サイドバー */}
        <div className="space-y-6">
          {/* 関連情報 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">関連情報</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>企業</p>
                {activity.accountName ? (
                  <Link 
                    to={`/accounts/${activity.accountId}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Building2 size={16} />
                    {activity.accountName}
                  </Link>
                ) : (
                  <p style={{ color: 'var(--text-primary)' }}>-</p>
                )}
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>案件</p>
                {activity.dealId && activity.dealName ? (
                  <Link 
                    to={`/deals/${activity.dealId}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Briefcase size={16} />
                    {activity.dealName}
                  </Link>
                ) : (
                  <p style={{ color: 'var(--text-primary)' }}>-</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 参加者 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users size={18} />
                参加者
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {activity.tsAttendees && (
                <div>
                  <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>TS側参加者</p>
                  <p style={{ color: 'var(--text-primary)' }}>{activity.tsAttendees}</p>
                </div>
              )}
              {participants.length > 0 && (
                <div>
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>先方参加者</p>
                  <div className="space-y-2">
                    {participants.map((p) => (
                      <div key={p.id} className="p-2 rounded bg-gray-50">
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {p.personName || '-'}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {[p.personCompany, p.personDept, p.personTitle].filter(Boolean).join(' / ')}
                        </p>
                        {p.personRole && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                            {p.personRole}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!activity.tsAttendees && participants.length === 0 && (
                <p style={{ color: 'var(--text-secondary)' }}>参加者情報がありません</p>
              )}
            </CardContent>
          </Card>

          {/* 詳細情報 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">詳細情報</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>商談ID</p>
                <p style={{ color: 'var(--text-primary)' }}>{activity.activityId}</p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>目的</p>
                <p style={{ color: 'var(--text-primary)' }}>{activity.activityPurpose || '-'}</p>
              </div>
              {activity.visitTo && (
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>訪問先</p>
                  <p style={{ color: 'var(--text-primary)' }}>{activity.visitTo}</p>
                </div>
              )}
              {activity.meetingMaterials && (
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>会議資料</p>
                  <p style={{ color: 'var(--text-primary)' }}>{activity.meetingMaterials}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
